// src/App.js
import React, { useState, useEffect } from 'react';
import { DecodeErc } from './ErcCalculation';
import JsonLdComponent from './JsonLdComponent';
import InlineStyles from './InlineStyles';
import ModelList from "./SupportComp";
import translations from './translations';
import PaymentModal from './PaymentModal';
import Nav from './components/Nav';
import { Link } from 'react-router-dom';
import { useMeta } from './useMeta';
import { 
  getCredits, 
  getRemainingFreeUnlocks, 
  canUnlock, 
  processUnlock 
} from './creditManager';

function App({ language = 'en' }) {
    const [inputCode, setInputCode] = useState('');
    const [decodedResult, setDecodedResult] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [credits, setCredits] = useState(getCredits());
    const [freeUnlocks, setFreeUnlocks] = useState(getRemainingFreeUnlocks());

    const t = translations[language] || translations['en'];

    const prefix = language === 'en' ? '' : `/${language}`;
    useMeta({
        title: language === 'en'
            ? 'Toyota ERC Unlock Code Generator | ercunlock.com'
            : `${t.title} | ERC Unlock`,
        description: language === 'en'
            ? 'Generate a free Toyota head unit ERC unlock code instantly. Enter your 16-digit serial number and unlock your Toyota stereo in seconds. Supports 53 models. No sign-up required.'
            : t.explanation,
        canonical: `https://ercunlock.com${prefix}/`
    });

    useEffect(() => {
        document.documentElement.lang = language;
        if (language === 'ur') {
            document.documentElement.dir = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
        }
        document.title = language === 'en'
            ? 'Toyota ERC Unlock Code Generator | ercunlock.com'
            : `${t.title} | ERC Unlock`;
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
            metaDescription.setAttribute('content', t.explanation);
        }

        const handleCreditsChanged = () => {
            setCredits(getCredits());
            setFreeUnlocks(getRemainingFreeUnlocks());
        };

        window.addEventListener('creditsChanged', handleCreditsChanged);
        return () => {
            window.removeEventListener('creditsChanged', handleCreditsChanged);
        };
    }, [language, t.title, t.explanation]);

    const handleSubmit = (e) => {
        e.preventDefault();

        if (inputCode.length !== 16) {
            setErrorMessage(t.errorMessage);
            setDecodedResult('');
            return;
        }

        if (!canUnlock()) {
            setShowPaymentModal(true);
            return;
        }

        if (processUnlock()) {
            setErrorMessage('');
            const result = DecodeErc(inputCode);
            setDecodedResult(result);
            setCredits(getCredits());
            setFreeUnlocks(getRemainingFreeUnlocks());
        } else {
            setErrorMessage('Failed to process unlock. Please try again.');
        }
    };

    const handleGenerateAnother = () => {
        setInputCode('');
        setDecodedResult('');
        setErrorMessage('');
    };

    const formatCreditsText = (text, count) => {
        return text.replace('{{count}}', count);
    };

    const nav = t.nav || {};

    return (
        <div className="app-container" data-lang={language} style={{ padding: 0, display: 'block' }}>
            <JsonLdComponent />
            <InlineStyles />
            <Nav language={language} />
            <div style={{ padding: '32px 16px', display: 'flex', justifyContent: 'center' }}>
            <div style={{ width: '100%', maxWidth: 960 }}>
                <div className="erc-decoder">
                    <h1>{t.title}</h1>

                    <div className="explanation">
                        <h4>{t.explanation}</h4>
                    </div>

                    {/* Credits Display */}
                    <div className="credits-display">
                        {freeUnlocks > 0 ? (
                            <div className="free-unlocks">
                                {formatCreditsText(t.credits.freeUnlocksRemaining, freeUnlocks)}
                            </div>
                        ) : credits > 0 ? (
                            <div className="paid-credits">
                                {formatCreditsText(t.credits.creditsAvailable, credits)}
                            </div>
                        ) : (
                            <div className="no-credits">
                                {t.credits.needCredits}
                                <button
                                    className="buy-credits-btn"
                                    onClick={() => setShowPaymentModal(true)}
                                >
                                    {t.credits.buyCredits}
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="navigationButtons">
                        <Link to={`${prefix}/what-is-erc`} style={{ textDecoration: 'none' }}>
                            <button type="button">{nav.whatIsErc || t.navigationButtons.whatIsErc}</button>
                        </Link>
                        <Link to={`${prefix}/how-to-find-erc-code`} style={{ textDecoration: 'none' }}>
                            <button type="button">{nav.howToFind || t.navigationButtons.howToFind}</button>
                        </Link>
                    </div>

                    <div className="erc-form">
                        <form onSubmit={handleSubmit}>
                            <label>
                                {t.inputLabel}
                                <input
                                    type="text"
                                    placeholder={t.inputPlaceholder}
                                    value={inputCode}
                                    onChange={(e) => setInputCode(e.target.value)}
                                    maxLength={16}
                                    minLength={16}
                                    required
                                />
                            </label>
                            <button id="ercDecodeButton" type="submit">{t.decodeButton}</button>
                            {errorMessage && <p className="error-message">{errorMessage}</p>}
                            {decodedResult && (
                                <div className="decoded-result">
                                    <p>Decoded ERC: {decodedResult}</p>
                                    <button className="generate-button" onClick={handleGenerateAnother}>
                                        {t.generateAnotherButton}
                                    </button>
                                </div>
                            )}
                        </form>
                    </div>

                    <div className='images' style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: 32 }}>
                        {/* Step 1 */}
                        <div style={{ textAlign: 'center', flex: '1 1 280px' }}>
                            <div style={{ background: 'var(--primary)', color: 'white', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 16, margin: '0 auto 10px' }}>1</div>
                            <img alt='locked out headunit' src="/images/SecurityQuestion.webp" />
                            <div style={{ marginTop: 10, background: '#f0f9ff', border: '2px solid #bae6fd', borderRadius: 10, padding: '10px 14px', maxWidth: 320, textAlign: 'left', margin: '10px auto 0' }}>
                                <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: '#0369a1' }}>{t.steps.step1.title}</p>
                                <p style={{ margin: '6px 0 0', fontSize: 12.5, color: '#0369a1' }}>{t.steps.step1.description}</p>
                            </div>
                        </div>

                        {/* Step 2 */}
                        <div style={{ textAlign: 'center', flex: '1 1 280px' }}>
                            <div style={{ background: 'var(--primary)', color: 'white', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 16, margin: '0 auto 10px' }}>2</div>
                            <img alt='stereo model number location' src="/images/modelfinder.webp" />
                            <div style={{ marginTop: 10, background: '#eff6ff', border: '2px solid var(--primary)', borderRadius: 10, padding: '10px 14px', maxWidth: 320, textAlign: 'left', margin: '10px auto 0' }}>
                                <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: 'var(--primary)' }}>{t.steps.step2.title}</p>
                                <p style={{ margin: '6px 0 0', fontSize: 12.5, color: '#1e40af' }}>{t.steps.step2.description}</p>
                                <p style={{ margin: '6px 0 0', fontSize: 12, color: '#1e40af', background: '#dbeafe', borderRadius: 6, padding: '6px 10px' }}>{t.steps.step2.note}</p>
                            </div>
                        </div>

                        {/* Step 3 */}
                        <div style={{ textAlign: 'center', flex: '1 1 280px' }}>
                            <div style={{ background: 'var(--primary)', color: 'white', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 16, margin: '0 auto 10px' }}>3</div>
                            <img alt='erc unlock screen' src="/images/ERCserial.webp" />
                            <div style={{ marginTop: 10, background: '#fffbeb', border: '2px solid #f59e0b', borderRadius: 10, padding: '10px 14px', maxWidth: 320, textAlign: 'left', margin: '10px auto 0' }}>
                                <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: '#92400e' }}>{t.steps.step3.title}</p>
                                <p style={{ margin: '6px 0 0', fontSize: 12.5, color: '#92400e' }}>{t.steps.step3.description}</p>
                            </div>
                        </div>
                    </div>

                    <div style={{ marginTop: '50px' }}>
                        <h1>{t.supportedModels.title}</h1>
                        <p>{t.supportedModels.description}</p>
                        <ModelList />
                    </div>

                    <div className="fb-like" data-href="https://www.facebook.com/people/ErcUnlockcom/61561749016493/" data-width="100" data-layout="" data-action="" data-size="" data-share="true"></div>

                    <div className="footer">
                        <div className="footer-links" style={{ display: 'flex', justifyContent: 'center', gap: 24, flexWrap: 'wrap' }}>
                            <Link to={`${prefix}/terms`} className="footer-link">Terms &amp; Conditions</Link>
                            <Link to={`${prefix}/privacy-policy`} className="footer-link">Privacy Policy</Link>
                            <Link to={`${prefix}/contact`} className="footer-link">Contact</Link>
                        </div>
                    </div>
                </div>
            </div>
            </div>

            {/* Payment Modal */}
            <PaymentModal
                isOpen={showPaymentModal}
                onClose={() => setShowPaymentModal(false)}
                language={language}
                onPurchaseSuccess={() => {
                    setCredits(getCredits());
                    setFreeUnlocks(getRemainingFreeUnlocks());
                }}
            />
        </div>
    );
}

export default App;
